export const useAuthStore = defineStore('auth', {
  state: () => {
    return {
      user: null,
      token: null,
      refresh: null,
      isLoggingOut: false,
    };
  },

  actions: {
    setAuth(payload) {
      if (payload) {
        const token = `Bearer ${payload.token}`;
        this.token = token;
        this.refresh = payload.refresh;
        this.user = payload.user;
        useCookie('token').value = token;
        useCookie('refresh').value = payload.refresh;
      } else {
        this.token = null;
        this.refresh = null;
        this.user = null;
        useCookie('token').value = null;
        useCookie('refresh').value = null;
      }
    },

    // Returns a discriminated result rather than either-a-user-or-a-challenge,
    // so callers switch on `status` instead of duck-typing the payload.
    async login({ email, password }) {
      const response = await useApi.post('/public/users/login', {
        body: {
          email,
          password,
          deviceToken: useCookie('deviceToken').value || undefined,
        },
      }).catch((error) => {
        console.error(error);
        if (error.response?.status === 403) {
          useToast.error(useT('bcd59'));
        } else if (error.response?.status === 498) {
          useToast.error(useT('bcd60'));
        } else {
          useToast.error(useT('bcd215'));
        }
      });

      if (!response) return { status: 'error' };

      if (response.twoFactorRequired) {
        return { status: 'twoFactorRequired', challenge: response };
      }

      this.setAuth(response);

      return { status: 'success', user: response.user };
    },

    async verifyLoginCode({ challengeId, code }) {
      const response = await useApi.post('/public/users/login/verify', {
        body: {
          challengeId,
          code,
        },
      }).catch((error) => {
        console.error(error);
        if (error.response?.status === 429) {
          useToast.error(useT('auth.twoFactor.error.locked'));
        } else if (error.response?.status === 403) {
          useToast.error(useT('auth.twoFactor.error.invalid'));
        } else {
          useToast.error(useT('bcd215'));
        }
      });

      if (!response) return false;

      this.setAuth(response);

      return response.user;
    },

    // Asked after the code is verified, so this runs on an authenticated
    // request. The cookie has to outlive the session cookies, so its maxAge
    // comes from the server's authoritative expiry rather than a duplicated
    // client-side constant.
    async trustCurrentDevice() {
      let status = null;

      const response = await useApi.post('/users/me/trusted-devices').catch((error) => {
        console.error(error);
        status = error.response?.status;

        // A 401 is already being turned into a logout and a redirect by
        // `useApi`, so this toast would be the second thing shouting about it.
        if (status !== 401) useToast.error(useT('auth.twoFactor.trust.error'));
      });

      // The outcome is reported rather than left for the caller to infer from
      // `isLoggedIn`: the logout `useApi` starts on a 401 clears that flag only
      // after its own round trip, so reading it here would race that chain.
      if (!response) return status === 401 ? 'unauthenticated' : 'failed';

      const maxAge = Math.round((new Date(response.deviceTokenExpiresAt).getTime() - Date.now()) / 1000);
      useCookie('deviceToken', { maxAge }).value = response.deviceToken;

      return 'trusted';
    },

    async resendLoginCode(challengeId) {
      const response = await useApi.post('/public/users/login/resend', {
        body: { challengeId },
      }).catch((error) => {
        console.error(error);
        if (error.response?.status === 429) {
          useToast.error(useT('auth.twoFactor.error.cooldown'));
        } else {
          useToast.error(useT('auth.twoFactor.error.resend'));
        }
      });

      if (response) {
        useToast(useT('auth.twoFactor.toast.resent'));
      }

      return response || false;
    },

    async logout() {
      // Several in-flight requests can 401 at once and each would call this.
      // Without the guard that means several revoke calls and several spurious
      // `userLoggedOut` audit entries for one logout.
      if (this.isLoggingOut) return;
      this.isLoggingOut = true;

      // Best-effort: the refresh token is revoked server-side and the logout is
      // audited, but a failure here must not strand the user in the app.
      if (this.token) {
        await useApi.post('/users/logout', {
          body: { refresh: this.refresh },
        }).catch(() => false);
      }

      this.setAuth(null);
      this.isLoggingOut = false;

      navigateTo('/login');
    },

    async fetchUser() {
      const response = await useApi.get('/users/me').catch(() => false);

      if (response) {
        this.user = response;

        return this.user;
      }

      return false;
    },

    async forgottenPassword({ email, lang }) {
      const response = await useApi.post('/public/users/forgotten-password', {
        body: {
          email,
          lang,
        },
      }).catch(() => {
        useToast.error(useT('bcd57'));
      });

      return response;
    },

    async newPassword({ email, password, token }) {
      const response = await useApi.post('/public/users/new-password', {
        body: {
          email,
          password,
          token,
        },
      }).catch(() => {
        useToast.error(useT('bcd56'));
      });

      return response;
    },
  },

  getters: {
    isLoggedIn(state) {
      return !!state.user;
    },
    hasRole(state) {
      return (role) => (Array.isArray(role) ? role.includes(state.user?.role) : state.user?.role === role);
    },
  },
});
